import { useState, useEffect, type ReactNode } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { supabase } from '@/lib/supabase';

interface Profile { id: string; created_at: string; role: string; }
interface ActivityLog { id: number; action: string; created_at: string; }
interface SavedNumber { generator_type: string; }

const COLORS = ['#D97706', '#0D9488', '#E11D48', '#78716C', '#059669'];

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="bg-background-50 rounded-2xl border border-background-200 p-4 md:p-5">
      <h4 className="font-heading text-sm text-foreground-700 mb-4">{title}</h4>
      {children}
    </div>
  );
}

export default function StatsCharts() {
  const [signupsByDay, setSignupsByDay] = useState<{ date: string; count: number }[]>([]);
  const [activityByDay, setActivityByDay] = useState<{ date: string; count: number }[]>([]);
  const [generatorRatio, setGeneratorRatio] = useState<{ name: string; value: number }[]>([]);
  const [totalMembers, setTotalMembers] = useState(0);
  const [totalActivities, setTotalActivities] = useState(0);
  const [totalNumbers, setTotalNumbers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [actionCounts, setActionCounts] = useState<{ name: string; value: number }[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const { data: profiles } = await supabase.from('profiles').select('created_at').order('created_at');
      const { data: logs } = await supabase.from('user_activity_logs').select('id,action,created_at').order('created_at');
      const { data: numbers } = await supabase.from('saved_numbers').select('generator_type');

      const dayMap = new Map<string, number>();
      const today = new Date();
      for (let i = 13; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        dayMap.set(d.toISOString().slice(0, 10), 0);
      }
      (profiles || []).forEach((p: Profile) => {
        const d = p.created_at.slice(0, 10);
        if (dayMap.has(d)) dayMap.set(d, (dayMap.get(d) || 0) + 1);
      });
      const signupData = Array.from(dayMap.entries()).map(([date, count]) => ({ date: date.slice(5), count }));
      setSignupsByDay(signupData);
      setTotalMembers(profiles?.length || 0);

      const actMap = new Map<string, number>();
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        actMap.set(d.toISOString().slice(0, 10), 0);
      }
      (logs || []).forEach((l: ActivityLog) => {
        const d = l.created_at.slice(0, 10);
        if (actMap.has(d)) actMap.set(d, (actMap.get(d) || 0) + 1);
      });
      const actData = Array.from(actMap.entries()).map(([date, count]) => ({ date: date.slice(5), count }));
      setActivityByDay(actData);
      setTotalActivities(logs?.length || 0);

      const genMap = new Map<string, number>();
      (numbers || []).forEach((n: SavedNumber) => {
        genMap.set(n.generator_type, (genMap.get(n.generator_type) || 0) + 1);
      });
      const genData = Array.from(genMap.entries()).map(([name, value]) => ({
        name: name === 'random' ? '랜덤' : name === 'lucky' ? '행운' : '수동',
        value,
      }));
      setGeneratorRatio(genData);
      setTotalNumbers(numbers?.length || 0);

      setLogs(logs || []);

      const actCountMap = new Map<string, number>();
      const actionNameMap: Record<string, string> = {
        login: '로그인',
        logout: '로그아웃',
        register: '회원가입',
        generate_random: '랜덤 생성',
        generate_lucky: '행운 생성',
        save_number: '번호 저장',
        delete_number: '번호 삭제',
      };
      (logs || []).forEach((l: ActivityLog) => {
        const label = actionNameMap[l.action] || l.action;
        actCountMap.set(label, (actCountMap.get(label) || 0) + 1);
      });
      const actCountData = Array.from(actCountMap.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name, value]) => ({ name, value }));
      setActionCounts(actCountData);

      setLoading(false);
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="w-10 h-10 mx-auto mb-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-foreground-400 text-sm">통계 데이터를 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-background-50 rounded-2xl border border-background-200 p-4 md:p-5">
          <p className="text-xs text-foreground-400 mb-1">총 회원</p>
          <p className="font-heading text-xl md:text-2xl text-foreground-800">{totalMembers.toLocaleString()}</p>
        </div>
        <div className="bg-background-50 rounded-2xl border border-background-200 p-4 md:p-5">
          <p className="text-xs text-foreground-400 mb-1">총 활동 이력</p>
          <p className="font-heading text-xl md:text-2xl text-foreground-800">{totalActivities.toLocaleString()}</p>
        </div>
        <div className="bg-background-50 rounded-2xl border border-background-200 p-4 md:p-5">
          <p className="text-xs text-foreground-400 mb-1">저장된 번호</p>
          <p className="font-heading text-xl md:text-2xl text-foreground-800">{totalNumbers.toLocaleString()}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <ChartCard title="최근 14일 가입자 추이">
          <div className="h-[200px] md:h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={signupsByDay}>
                <defs>
                  <linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS[0]} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="count" stroke={COLORS[0]} fill="url(#signupGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="최근 7일 활동량">
          <div className="h-[200px] md:h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={activityByDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Bar dataKey="count" fill={COLORS[1]} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="번호 생성 방식 비율">
          <div className="h-[200px] md:h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={generatorRatio}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={70}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {generatorRatio.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Legend verticalAlign="bottom" height={36} iconType="circle" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="활동 유형 Top 5">
          <div className="h-[200px] md:h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={actionCounts} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#4B5563' }} width={70} />
                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Bar dataKey="value" fill={COLORS[3]} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>
    </div>
  );
}